import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'

import type { CampaignModel } from '@models/types'
import type { CampaignModuleState } from '@redux/modules/campaign'

const watchEntityKeys = [
  'campaignContributions',
  'campaignItems',
  'campaignQuotes',
  'creditNotes',
  'invoices',
  'orders',
]

// Dont sort - specifically in this order
const availableItems: { [key: string]: { title: string, type: string, isBalance?: boolean } } = {
  approved_quote_total: {
    title: 'Approved Quotes',
    type: 'currency',
  },
  approved_quote_count: {
    title: 'Approved Quotes (Count)',
    type: 'count',
  },
  approved_order_total: {
    title: 'Approved Orders',
    type: 'currency',
  },
  approved_order_count: {
    title: 'Approved Orders (Count)',
    type: 'count',
  },
  campaign_item_total: {
    title: 'Custom Items',
    type: 'currency',
  },
  campaign_item_count: {
    title: 'Custom Items (Count)',
    type: 'count',
  },
  item_count: {
    title: 'Ordered Items (Count)',
    type: 'count',
  },
  remaining_budget: {
    title: 'Budget Remaining',
    type: 'currency',
  },
  approved_invoice_total: {
    title: 'Invoiced',
    type: 'currency',
  },
  approved_bill_total: {
    title: 'Bills',
    type: 'currency',
  },
  contribution_total: {
    title: 'Contributions (Total)',
    type: 'currency',
  },
  received_contribution_total: {
    title: 'Contributions (Received)',
    type: 'currency',
  },
  credit_note_total: {
    title: 'Credit Notes',
    type: 'currency',
  },
  payment_total: {
    title: 'Payment Total',
    type: 'currency',
  },
  outstanding_contributions: {
    title: 'Outstanding Contributions',
    type: 'currency',
  },
  total_combined_spend: {
    title: 'Total Spend',
    type: 'currency',
  },
  campaign_balance: {
    title: 'Balance',
    type: 'currency',
    isBalance: true,
  },
}

type UseCampaignReconciliationTotalsOptions = {
  performHttpRequests: boolean,
}

function useCampaignReconciliationTotals(
  campaign: CampaignModel,
  options: UseCampaignReconciliationTotalsOptions,
) {
  const { performHttpRequests = false } = options

  const {
    updatedEntities: {
      campaignContributions: campaignContributionsUpdatedAt,
      campaignItems: campaignItemsUpdatedAt,
      campaignQuotes: campaignQuotesUpdatedAt,
      creditNotes: creditNotesUpdatedAt,
      invoices: invoicesUpdatedAt,
      orders: ordersUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { loading: loadingCampaignReconciliationTotals } = useReduxAction(
    'campaigns',
    'loadCampaignReconciliationTotals',
    {},
    [
      campaign.id,
      performHttpRequests,
      campaignContributionsUpdatedAt,
      campaignItemsUpdatedAt,
      campaignQuotesUpdatedAt,
      creditNotesUpdatedAt,
      invoicesUpdatedAt,
      ordersUpdatedAt,
    ],
    {
      dispatchAction: (action, requestOptions) => action(campaign.id, requestOptions),
      // @ts-expect-error TS2322
      shouldPerformFn: ({ loadingReconciliationTotals }: CampaignModuleState) => (
        !!performHttpRequests && !!campaign.id && !loadingReconciliationTotals
      ),
    },
  )

  const totals = campaign.reconciliation_totals
  const inCredit = totals?.in_credit || false

  return {
    availableItems,
    campaign,
    inCredit,
    loading: loadingCampaignReconciliationTotals,
    totals: totals || {},
  }
}

export default useCampaignReconciliationTotals
